import axios from 'axios';
import apiClient from './apiClient';
import type { User } from '../store/slices/authSlice'; // Import User type
import { AuthService } from '../api';
import { setOpenApiToken } from '../api/core/OpenAPI';

// Define types for credentials and user data based on backend expectations
export interface LoginCredentials {
  // Exporting for authSlice
  username: string; // Use username instead of email
  password: string; // Password is required
}

export interface RegisterData {
  // Exporting for authSlice
  username?: string; // Should be required by backend
  password?: string; // Should be required by backend
  // No email field as owner creation only requires username and password
}

// Expected response structure for login, aligning with AuthResponseData in authSlice
interface LoginResponse {
  user: User;
  accessToken: string;
}

// Expected response structure for registration (assuming it returns the created user)
interface RegisterResponse {
  user: User;
  // Potentially an access token if backend auto-logs in, but current slice doesn't assume this
}

// Using setAuthHeader from apiClient.ts

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  try {
    // Note: Backend returns { access_token: string }, not the expected LoginResponse format
    const response = await AuthService.authControllerLogin(credentials);
    // Extract JWT payload to get user information
    const token = response.access_token;
    if (!token) {
      throw new Error('Login succeeded but no access token was returned');
    }
    // Todo: proper jwt parsing and validation
    // Get user info from JWT payload
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));

    // Transform backend response to match expected LoginResponse interface
    const transformedResponse: LoginResponse = {
      user: {
        id: payload.sub,
        username: payload.username,
        email: '', // Email might not be in JWT, using empty string as fallback
        roles: payload.roles || [],
        features: payload.features || [], // Add features from JWT
      },
      accessToken: token,
    };
    setOpenApiToken(token); // Set the token in OpenAPI client

    // setAuthHeader is called by the slice after successful login
    return transformedResponse;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      // Re-throw the error object so the slice can access error.response.data
      throw error;
    }
    throw new Error('Login failed due to an unexpected error.');
  }
};

export const register = async (userData: RegisterData): Promise<RegisterResponse> => {
  try {
    const response = await apiClient.post<RegisterResponse>('/auth/register', userData);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      // Re-throw the error object so the slice can access error.response.data
      throw error;
    }
    throw new Error('Registration failed due to an unexpected error.');
  }
};

// Example of a protected route call, can be added later
// export const fetchUserProfile = async () => {
//   try {
//     const response = await apiClient.get('/profile'); // Example endpoint
//     return response.data;
//   } catch (error) {
//     if (axios.isAxiosError(error) && error.response) {
//       throw new Error(error.response.data.message || 'Failed to fetch profile');
//     }
//     throw new Error('Failed to fetch profile due to an unexpected error.');
//   }
// };

// No longer exporting apiClient, as it's imported from apiClient.ts
